import { useState, useRef, useEffect } from 'react'

/**
 * InstantModeSwitch — switch protegido estilo "guarded switch" de aviación: el
 * interruptor vive bajo una "tapa". Click 1 = levantar la tapa (queda armado y vuelve
 * a bloquearse solo a los 4s sin acción). Click 2 (sobre el switch armado) = alternar.
 *
 * Controla el modo instantáneo de una campaña: ON = las inscripciones nuevas envían
 * sus pasos de inmediato y encadenados, ignorando el calendario.
 *
 * stopPropagation siempre: vive dentro de tarjetas clickeables (CampaignsView).
 *
 * Props:
 *   enabled: boolean        — estado actual del modo instantáneo
 *   onToggle(next: boolean) — llamado SOLO tras el doble click de confirmación
 *   busy: boolean           — request en vuelo (deshabilita)
 */
export default function InstantModeSwitch({ enabled, onToggle, busy = false }) {
  const [armed, setArmed] = useState(false)
  const timerRef = useRef(null)
  useEffect(() => () => clearTimeout(timerRef.current), [])

  const arm = (e) => {
    e.stopPropagation()
    if (busy) return
    setArmed(true)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setArmed(false), 4000)
  }

  const fire = (e) => {
    e.stopPropagation()
    if (busy) return
    clearTimeout(timerRef.current)
    setArmed(false)
    onToggle(!enabled)
  }

  // UN solo botón con transiciones (no dos botones intercambiados, que se veía
  // repentino): cada pieza colapsa/expande vía max-w + opacity + transform en 300ms.
  // Tapa abajo (bloqueado): ⚡ + "Instantánea" (si ON) + candado. Click = armar.
  // Armado: el candado "se levanta" (rota y se encoge), el switch real emerge; 4s
  // para confirmar o se re-bloquea con la animación inversa.
  return (
    <button
      type="button"
      onClick={armed ? fire : arm}
      disabled={busy}
      title={armed
        ? (enabled ? 'Click para DESACTIVAR el modo instantáneo' : 'Click para ACTIVAR el modo instantáneo')
        : (enabled
          ? 'Modo instantáneo ACTIVO: las inscripciones nuevas envían sus pasos de inmediato. Click para desbloquear el switch.'
          : 'Modo instantáneo apagado (los pasos respetan el calendario). Click para desbloquear el switch.')}
      className={`inline-flex items-center px-2 py-1 rounded-lg border text-xs font-medium select-none disabled:opacity-50 transition-all duration-300 ease-out ${
        armed
          ? 'border-transparent ring-2 ring-amber-400 bg-amber-50 dark:bg-amber-900/20 animate-pulse'
          : enabled
            ? 'bg-amber-100 border-amber-400 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:border-amber-600 dark:text-amber-300'
            : 'bg-gray-100 border-gray-300 text-gray-400 hover:bg-gray-200 dark:bg-gray-800 dark:border-gray-600'
      }`}
    >
      <span aria-hidden="true" className={`transition-transform duration-300 ${armed ? 'scale-110' : ''}`}>⚡</span>

      {/* Label "Instantánea" (solo bloqueado + ON): colapsa al armar */}
      <span className={`overflow-hidden whitespace-nowrap transition-all duration-300 ease-out ${
        !armed && enabled ? 'max-w-[90px] opacity-100 ml-1' : 'max-w-0 opacity-0 ml-0'
      }`}>Instantánea</span>

      {/* Candado (la "tapa"): al armar se levanta — rota, sube y se desvanece */}
      <span className={`inline-flex overflow-hidden transition-all duration-300 ease-out ${
        armed ? 'max-w-0 opacity-0 -rotate-45 -translate-y-1 scale-50 ml-0' : 'max-w-[16px] opacity-100 ml-1'
      }`} aria-hidden="true">
        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </span>

      {/* Switch real + acción: emerge al armar, colapsa al re-bloquear */}
      <span className={`inline-flex items-center overflow-hidden transition-all duration-300 ease-out ${
        armed ? 'max-w-[110px] opacity-100 ml-1.5' : 'max-w-0 opacity-0 ml-0'
      }`}>
        <span className={`relative inline-flex h-4 w-8 flex-shrink-0 rounded-full transition-colors duration-300 ${enabled ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
          <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-all duration-300 ${enabled ? 'left-4' : 'left-0.5'}`} />
        </span>
        <span className="ml-1.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300 whitespace-nowrap">{enabled ? 'apagar' : 'encender'}</span>
      </span>
    </button>
  )
}
